import React from "react";
import "./authorizationForRenewal.scss";
import TextInput from "../../sharedComponents/textInput/textInput";
import Validator, { ValidatedValue, ValidatorFunction } from "../../../helpers/validator";
import { ErrorMessages } from "../../../helpers/errorMessage";
import { CapitalizationType } from "../../../helpers/formatter";
import { Button } from "@material-ui/core";
import Loader from "../../sharedComponents/loader/Loader";
import { RetrieveCalculationService } from "../../../services/services/retrieveCalculationService";
import { inject, observer } from "mobx-react";
import { AuthorizationRenewalData } from "../../../helpers/renewal";
import { HttpError } from "../../sharedComponents/notification/httpError";

interface AuthorizationForRenewalProps {
  retrieveCalculationService?: RetrieveCalculationService;
}

interface AuthorizationForRenewalState {
  isPerson: boolean;
  retrieveUuid: string;
  password: ValidatedValue<string>;
  isPending: boolean;
}

@inject("retrieveCalculationService")
@observer
export default class AuthorizationForRenewal extends React.Component<AuthorizationForRenewalProps, AuthorizationForRenewalState> {
  constructor(props: any) {
    super(props);
    const { isPerson, retrieveUuid } = this.getDataFromUrl();

    this.state = {
      isPerson,
      retrieveUuid,
      password: new ValidatedValue<string>("", this.getValidators(isPerson)),
      isPending: false
    };
  }

  public render(): JSX.Element {
    return (
      <div className="authorization-for-renewal">
        <div>
          W zawiązku z tym, że oferta odnowienia zawiera Twoje dane, a nie chcemy by osoby niepowołane miały
          do nich dostęp zabezpieczyliśmy je hasłem.
        </div>

        <div>Aby otworzyć propozycję odnowienia wprowadź:</div>

        {this.state.isPerson ? this.renderContentForPerson() : this.renderContentForCompany()}

        <TextInput
          className="password-input"
          validatedValue={this.state.password}
          capitalize={CapitalizationType.standard}
        />

        <div>
          Jeżeli hasło nie działa, uprzejmie prosimy o kontakt z Działem Obsługi Klienta TUZ Ubezpieczenia
          telefonicznie pod numerem <a className="link" href="tel:+48223276060">22 327 60 60</a> lub mailowo
          na adres <a className="link" href="mailto:bok@tuz.pl">bok@tuz.pl</a>.
        </div>

        <div>
          Skorzystanie z usługi jest jednoznaczne z akceptacją <a
            className="link" target="_blank" rel="noopener noreferrer"
            href="https://tuz.pl/strefa-klienta/regulaminy/">Regulaminu</a> świadczenia usług drogą
          elektroniczną.
        </div>

        <div className="actions">
          <div className="error-message-with-button">
            {this.props.retrieveCalculationService!.authorizeRetrieveContractForRenewalRequestStatus.isError && this.renderHttpErrorMessage()}

            <Button variant="contained"
              className={`retrieve-button ${this.state.isPending ? "disabled" : ""}`}
              onClick={() => this.authorize()}
              disabled={this.state.isPending}
            >
              {this.state.isPending ? <Loader /> : <span>Wczytaj</span>}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  private renderContentForPerson(): JSX.Element {
    return <div className="content">
      <div>
        Miejscowość/pierwszy człon nazwy miejscowości wskazanej do korespondencji (bez polskich znaków i z dużej
        litery), postawić znak „@” oraz wpisać datę urodzenia ubezpieczającego w formacie DDMMRRRR
      </div>

      <div className="examples">
        <div>
          <u>Przykład 1</u>: Klient wskazuje adres do korespondencji w Warszawie i urodził się 03.12.1987r.
          Wówczas jego hasło to Warszawa@03121987.
        </div>
        <div>
          <u>Przykład 2</u>: Klient wskazuje adres do korespondencji w miejscowości Jastrzębie-Zdrój i urodził
          się 03.12.1987r. Wówczas jego hasło to Jastrzebie@03121987.
        </div>
      </div>
    </div>;
  }

  private renderContentForCompany(): JSX.Element {
    return <div className="content">
      <div>
        Miejscowość/pierwszy człon nazwy miejscowości prowadzenia działalności (bez polskich znaków i z dużej
        litery), postawić znak „@” oraz wpisać numer REGON
      </div>

      <div className="examples">
        <div>
          <u>Przykład 1</u>: Klient prowadzi działalność o nr REGON 123456789 w Warszawie. Wówczas
          jego hasło to Warszawa@123456789.
        </div>
        <div>
          <u>Przykład 2</u>: Klient prowadzi działalność o nr REGON 123456789 w miejscowośi Jastrzębie-Zdrój.
          Wówczas jego hasło to Jastrzebie@123456789.
        </div>
      </div>
    </div>;
  }

  private renderHttpErrorMessage(): JSX.Element {
    return <HttpError
      customizedErrorMessage="Wystąpił błąd poczas autoryzacji"
      type={"error"}
    />;
  }

  private getDataFromUrl(): { isPerson: boolean, retrieveUuid: string } {
    const url = new URL(window.location.href);
    const isPerson = !!url.searchParams.get("p");
    const retrieveUuid = (isPerson ? url.searchParams.get("p") : url.searchParams.get("c")) || "";

    return { isPerson, retrieveUuid };
  }

  private getValidators(isPerson: boolean): ValidatorFunction[] {
    const personPasswordValidators = [
      { name: "isNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required },
      {
        name: "isCorrect",
        fn: Validator.passwordForRenewalForPerson,
        errorMessage: ErrorMessages.renewalPassword
      }
    ];

    const companyPasswordValidators = [
      { name: "isNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required },
      {
        name: "isCorrect",
        fn: Validator.passwordForRenewalForCompany,
        errorMessage: ErrorMessages.renewalPassword
      }
    ];

    return isPerson ? personPasswordValidators : companyPasswordValidators;
  }

  private async authorize(): Promise<void> {
    this.state.password.validate();

    if (!this.state.password.errorMessage) {
      this.setState({ isPending: true });

      const response = await this.props.retrieveCalculationService!.authorizeRetrieveContractForRenewalRequest(
        this.state.retrieveUuid,
        this.mapPasswordToAuthorizationRenewalData()
      );

      this.setState({ isPending: false });

      if (response?.authorizedPolicyRenewalOfferId) {
        this.goToFirstCalculatorsPageWithRenewalOfferId(response.authorizedPolicyRenewalOfferId);
      }
    }
  }

  private goToFirstCalculatorsPageWithRenewalOfferId(renewalOfferId: string): void {
    const url = new URL(window.location.href);
    url.searchParams.append("renewalOfferId", renewalOfferId);
    window.location.href = url.href;
  }

  private mapPasswordToAuthorizationRenewalData(): AuthorizationRenewalData {
    const partsOfPassword = this.state.password.value.split("@");
    const firstPartOfLocality = partsOfPassword[0];

    if (this.state.isPerson) {
      const dateOfBirth = partsOfPassword[1];
      const day = dateOfBirth?.substring(0, 2);
      const month = dateOfBirth?.substring(2, 4);
      const year = dateOfBirth?.substring(4);
      const dateIsNumber = year && month && day && !isNaN(Number(year)) && !isNaN(Number(month)) && !isNaN(Number(day));

      return {
        firstPartOfLocality,
        dateOfBirth: dateIsNumber ? `${year}-${month}-${day}` : undefined
      };
    } else {
      const regonIsNumber = partsOfPassword[1] && !isNaN(Number(partsOfPassword[1]));

      return {
        firstPartOfLocality,
        regon: regonIsNumber ? partsOfPassword[1] : undefined
      };
    }
  }
}
